import { memo, useCallback, useRef, useState } from 'react';

// @ts-expect-error
import ReactCrop from 'react-image-crop';

import 'react-image-crop/dist/ReactCrop.css';

import { Button } from '@/shadcn/button';
import { DialogClose, DialogFooter } from '@/shadcn/dialog';

import { TestId } from '@/types/testId';

const croppedFileName = 'cropped.png' as const;

type CropFile = {
  url: string | null;
  file: File | null;
};

type Props = {
  imageSrc: string;
  actionLabel?: string;
  submit: (cropFile: CropFile) => void;
  cancel: () => void;
};

export const CropImage = memo(
  ({ imageSrc, actionLabel, submit, cancel }: Props) => {
    const [cropState, setCropState] = useState<ReactCrop.Crop>({
      aspect: 1
    }); // Make sure image is cropped as a square.

    const [cropFile, setCropFile] = useState<CropFile>({
      url: null,
      file: null
    });

    const loadedImageRef = useRef<HTMLImageElement | null>(null);

    const onImageLoaded = useCallback((target: HTMLImageElement) => {
      loadedImageRef.current = target;
    }, []);

    const onCropChange = useCallback(
      (crop: ReactCrop.Crop) => setCropState(crop),
      []
    );

    const onCropComplete = useCallback(
      (crop: ReactCrop.Crop) => {
        if (loadedImageRef.current && crop.width && crop.height) {
          const canvas = document.createElement('canvas');
          const scaleX =
            loadedImageRef.current.naturalWidth / loadedImageRef.current.width;
          const scaleY =
            loadedImageRef.current.naturalHeight /
            loadedImageRef.current.height;
          canvas.width = crop.width;
          canvas.height = crop.height;
          const ctx = canvas.getContext('2d');

          if (!ctx) {
            console.error('No canvas context found');
            return;
          }

          ctx.drawImage(
            loadedImageRef.current,
            (crop.x ?? 0) * scaleX,
            (crop.y ?? 0) * scaleY,
            crop.width * scaleX,
            crop.height * scaleY,
            0,
            0,
            crop.width,
            crop.height
          );

          const { url } = cropFile;
          canvas.toBlob(blob => {
            if (!blob) {
              console.error('No blob found');
              return;
            }
            // @ts-expect-error
            // blob.name = croppedFileName;
            if (url) {
              window.URL.revokeObjectURL(url);
            }
            const fileUrl = window.URL.createObjectURL(blob);
            const fileObj = new File([blob], croppedFileName, {
              type: 'image/png'
            });
            setCropFile({
              url: fileUrl,
              file: fileObj
            });
          }, 'image/png');
        }
      },
      [cropFile]
    );

    const onSubmit = useCallback(() => submit(cropFile), [cropFile, submit]);

    return (
      <>
        <section className="relative flex justify-center">
          <ReactCrop
            className=""
            crop={cropState}
            onChange={onCropChange}
            onComplete={onCropComplete}
            onImageLoaded={onImageLoaded}
            src={imageSrc}
          />
        </section>
        <DialogFooter>
          <DialogClose asChild>
            <Button
              data-testid={TestId.CropImage.CancelButton}
              onClick={cancel}
              variant="outline"
            >
              Cancel
            </Button>
          </DialogClose>

          <Button
            data-testid={TestId.CropImage.SubmitButton}
            disabled={
              !((cropState.height ?? 0) > 0 && (cropState.width ?? 0) > 0)
            }
            onClick={onSubmit}
          >
            {actionLabel}
          </Button>
        </DialogFooter>
      </>
    );
  }
);
